'use client';

import { Skeleton } from '@mui/material';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useCanWorkspaceAction } from '@/features/workspaces/hooks/useCanWorkspaceAction';
import { useWorkspaceRoutes } from '@/hooks/useWorkspaceRoutes';
import { EmptyState, SecondaryButton } from '@web/ui';
import { getErrorMessage, notify } from '@web/utils';

import {
  useDeleteJobMutation,
  useLazyGetJobsQuery,
  useTransitionJobMutation,
} from '../api/jobsApi';

import { JobCard } from './JobCard';
import styles from './JobsCardsList.module.scss';

import type { Job, JobStatus } from '@/features/jobs/types';

type JobsCardsListProps = {
  search: string;
  status: JobStatus | 'all';
  jobKind: string;
};

type LoadMode = 'replace' | 'append';

const PAGE_SIZE = 20;
const LOADING_CARD_COUNT = 4;
const ALL_STATUSES_QUERY = 'pending,active,completed';

function getNextPage(nextUrl: string | null) {
  if (!nextUrl) {
    return null;
  }

  try {
    const parsed = nextUrl.startsWith('http')
      ? new URL(nextUrl)
      : new URL(nextUrl, 'http://localhost');
    const pageValue = parsed.searchParams.get('page');
    if (!pageValue) {
      return null;
    }
    const page = Number(pageValue);
    return Number.isNaN(page) ? null : page;
  } catch {
    return null;
  }
}

function dedupeById(rows: Job[]) {
  const byId = new Map<string, Job>();
  rows.forEach((row) => byId.set(row.id, row));
  return Array.from(byId.values());
}

export function JobsCardsList({ search, status, jobKind }: JobsCardsListProps) {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [nextPage, setNextPage] = useState<number | null>(1);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const isFetchingRef = useRef(false);
  const requestVersionRef = useRef(0);

  const router = useRouter();
  const workspaceRoutes = useWorkspaceRoutes();
  const canCreateJobs = useCanWorkspaceAction(
    'job_management',
    'create',
    false,
  );
  const canDeleteJobs = useCanWorkspaceAction(
    'job_management',
    'delete',
    false,
  );
  const canTransitionJobs = useCanWorkspaceAction(
    'job_transition_management',
    'edit',
    false,
  );
  const [loadJobs] = useLazyGetJobsQuery();
  const [transitionJob, { isLoading: isTransitioning }] =
    useTransitionJobMutation();
  const [deleteJob, { isLoading: isDeletingJob }] = useDeleteJobMutation();

  const loadPage = useCallback(
    async (page: number, mode: LoadMode) => {
      if (isFetchingRef.current && mode === 'append') {
        return;
      }

      isFetchingRef.current = true;
      setLoadError(null);

      if (mode === 'replace') {
        setIsInitialLoading(true);
      } else {
        setIsLoadingMore(true);
      }

      const requestVersion = requestVersionRef.current;

      try {
        const statusParam =
          status === 'all' ? ALL_STATUSES_QUERY : status || undefined;

        const response = await loadJobs(
          {
            query: search || undefined,
            status: statusParam,
            job_kind: jobKind !== 'all' ? jobKind : undefined,
            page,
            page_size: PAGE_SIZE,
          },
          false,
        ).unwrap();

        if (requestVersion !== requestVersionRef.current) {
          return;
        }

        const incoming = response.data?.results ?? [];
        setJobs((current) =>
          mode === 'replace' ? incoming : dedupeById([...current, ...incoming]),
        );
        setNextPage(getNextPage(response.data?.next ?? null));
      } catch (error) {
        if (requestVersion !== requestVersionRef.current) {
          return;
        }
        const message = getErrorMessage(error);
        setLoadError(message);
        notify({ type: 'error', message });
      } finally {
        isFetchingRef.current = false;
        setIsInitialLoading(false);
        setIsLoadingMore(false);
      }
    },
    [jobKind, loadJobs, search, status],
  );

  useEffect(() => {
    requestVersionRef.current += 1;
    isFetchingRef.current = false;
    setJobs([]);
    setNextPage(1);
    void loadPage(1, 'replace');
  }, [jobKind, loadPage, search, status]);

  useEffect(() => {
    const target = sentinelRef.current;
    if (!target || !nextPage || isInitialLoading || isLoadingMore) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && nextPage) {
          void loadPage(nextPage, 'append');
        }
      },
      {
        root: null,
        rootMargin: '220px',
        threshold: 0,
      },
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [isInitialLoading, isLoadingMore, loadPage, nextPage]);

  const handleOpenJob = useCallback(
    (jobId: string) => {
      const selectedJob = jobs.find((job) => job.id === jobId);

      if (selectedJob?.status === 'pending') {
        router.push(workspaceRoutes.jobs.detailPlan(jobId));
        return;
      }

      if (selectedJob?.status === 'active') {
        router.push(workspaceRoutes.jobs.detailTasks(jobId));
        return;
      }

      if (selectedJob?.status === 'completed') {
        router.push(workspaceRoutes.jobs.detailPerformance(jobId));
        return;
      }

      if (selectedJob?.status === 'archived') {
        router.push(workspaceRoutes.jobs.detailSettings(jobId));
        return;
      }

      router.push(workspaceRoutes.jobs.detail(jobId));
    },
    [jobs, router, workspaceRoutes.jobs],
  );

  const handleTransition = useCallback(
    async (jobId: string, to: JobStatus) => {
      try {
        const response = await transitionJob({ jobId, to }).unwrap();
        notify({
          type: 'success',
          message: response.message || 'Job updated.',
        });
        await loadPage(1, 'replace');
      } catch (error) {
        notify({ type: 'error', message: getErrorMessage(error) });
      }
    },
    [loadPage, transitionJob],
  );

  const handleComplete = useCallback(
    async (jobId: string, completionDate: string) => {
      try {
        const response = await transitionJob({
          jobId,
          to: 'completed',
          completed_at: completionDate,
        }).unwrap();
        notify({
          type: 'success',
          message: response.message || 'Job completed.',
        });
        await loadPage(1, 'replace');
      } catch (error) {
        notify({ type: 'error', message: getErrorMessage(error) });
      }
    },
    [loadPage, transitionJob],
  );

  const handleDelete = useCallback(
    async (jobId: string) => {
      try {
        const response = await deleteJob(jobId).unwrap();
        notify({
          type: 'success',
          message: response.message || 'Job deleted.',
        });
        await loadPage(1, 'replace');
      } catch (error) {
        notify({ type: 'error', message: getErrorMessage(error) });
      }
    },
    [deleteJob, loadPage],
  );

  if (isInitialLoading && jobs.length === 0) {
    return (
      <div className={styles.jobsCardsList}>
        {Array.from({ length: LOADING_CARD_COUNT }).map((_, index) => (
          <div
            key={`job-card-skeleton-${index}`}
            className={styles.jobsCardsList__skeletonCard}
            data-testid="job-card-skeleton"
          >
            <div className={styles.jobsCardsList__skeletonLeft}>
              <Skeleton variant="text" width={80} height={18} />
              <Skeleton variant="text" width="58%" height={44} />
              <div className={styles.jobsCardsList__skeletonMeta}>
                <Skeleton variant="rounded" width={74} height={28} />
                <Skeleton variant="rounded" width={74} height={28} />
                <Skeleton variant="text" width={220} height={24} />
              </div>
            </div>

            <div className={styles.jobsCardsList__skeletonRight}>
              <Skeleton variant="text" width={96} height={18} />
              <Skeleton variant="text" width={74} height={42} />
              <Skeleton variant="circular" width={30} height={30} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!isInitialLoading && jobs.length === 0) {
    return (
      <div className={styles.jobsCardsList__empty}>
        <EmptyState
          title="No jobs found."
          description="Create a new job to get started."
          actions={
            canCreateJobs ? (
              <SecondaryButton
                ariaLabel="Start first job"
                onClick={() => router.push(workspaceRoutes.jobs.new)}
              >
                Start first job
              </SecondaryButton>
            ) : null
          }
        />
      </div>
    );
  }

  return (
    <div className={styles.jobsCardsList}>
      {jobs.map((job) => (
        <JobCard
          key={job.id}
          job={job}
          onOpen={handleOpenJob}
          onOpenSettings={(jobId) =>
            router.push(workspaceRoutes.jobs.detailSettings(jobId))
          }
          onTransition={handleTransition}
          onComplete={handleComplete}
          onDelete={handleDelete}
          onDuplicated={() => loadPage(1, 'replace')}
          isTransitioning={isTransitioning}
          isCompleting={isTransitioning}
          isDeleting={isDeletingJob}
          canTransitionJobs={canTransitionJobs}
          canDeleteJobs={canDeleteJobs}
          canDuplicateJobs={canCreateJobs}
        />
      ))}

      {loadError ? (
        <p className={styles.jobsCardsList__error}>
          Failed to load more jobs: {loadError}
        </p>
      ) : null}

      {isLoadingMore ? (
        <p className={styles.jobsCardsList__loadingMore}>Loading more...</p>
      ) : null}

      {nextPage ? (
        <div
          ref={sentinelRef}
          aria-label="jobs-infinite-scroll-sentinel"
          className={styles.jobsCardsList__sentinel}
        />
      ) : null}
    </div>
  );
}
